import { createAction, props } from '@ngrx/store';
import {
  IFireSightBootstrap,
  IFireSightHistoryDateFilter,
  IFireSightIncidentDetail,
  IFireSightIncidentSummary,
  IFireSightMonitoringArea,
  IFireSightObservation,
  IFireSightSimulation,
  IFireSightWebSocketEvent,
  IFireSightWebSocketStatusChange,
  TFireSightActiveView,
  TFireSightOverviewView,
} from '../../../interfaces/fire-sight/IFireSightModels';

export const enterFireSight = createAction(
  '[FireSight] Enter',
  props<{ projectUuid: string }>(),
);
export const leaveFireSight = createAction('[FireSight] Leave');

export const loadFireSightBootstrap = createAction(
  '[FireSight] Load Bootstrap',
  props<{ projectUuid: string }>(),
);
export const loadFireSightBootstrapSuccess = createAction(
  '[FireSight] Load Bootstrap Success',
  props<{ bootstrap: IFireSightBootstrap }>(),
);
export const loadFireSightBootstrapFailure = createAction(
  '[FireSight] Load Bootstrap Failure',
  props<{ error: string }>(),
);

export const loadFireSightMonitoringAreas = createAction(
  '[FireSight] Load Monitoring Areas',
  props<{ projectUuid: string }>(),
);
export const loadFireSightMonitoringAreasSuccess = createAction(
  '[FireSight] Load Monitoring Areas Success',
  props<{ monitoringAreas: IFireSightMonitoringArea[] }>(),
);
export const loadFireSightMonitoringAreasFailure = createAction(
  '[FireSight] Load Monitoring Areas Failure',
  props<{ error: string }>(),
);

export const setFireSightOverviewView = createAction(
  '[FireSight] Set Overview View',
  props<{ overviewView: TFireSightOverviewView }>(),
);
export const setFireSightHistoryDateFilter = createAction(
  '[FireSight] Set History Date Filter',
  props<{ dateFilter: IFireSightHistoryDateFilter | null }>(),
);
export const setFireSightHistoryDisplayNameFilter = createAction(
  '[FireSight] Set History Display Name Filter',
  props<{ displayName: string }>(),
);
export const setFireSightHistoryCentroidsVisible = createAction(
  '[FireSight] Set History Centroids Visible',
  props<{ visible: boolean }>(),
);
export const loadFireSightHistoricalIncidents = createAction(
  '[FireSight] Load Historical Incidents',
  props<{ projectUuid: string }>(),
);
export const loadFireSightHistoricalIncidentsSuccess = createAction(
  '[FireSight] Load Historical Incidents Success',
  props<{
    incidents: IFireSightIncidentSummary[];
    total: number;
    nextCursor: string | null;
    hasMore: boolean;
    queryKey: string;
  }>(),
);
export const loadFireSightHistoricalIncidentsFailure = createAction(
  '[FireSight] Load Historical Incidents Failure',
  props<{ error: string; queryKey: string }>(),
);
export const loadMoreFireSightHistoricalIncidents = createAction(
  '[FireSight] Load More Historical Incidents',
);
export const loadMoreFireSightHistoricalIncidentsSuccess = createAction(
  '[FireSight] Load More Historical Incidents Success',
  props<{
    incidents: IFireSightIncidentSummary[];
    total: number;
    nextCursor: string | null;
    hasMore: boolean;
    queryKey: string;
    requestedCursor: string;
  }>(),
);
export const loadMoreFireSightHistoricalIncidentsFailure = createAction(
  '[FireSight] Load More Historical Incidents Failure',
  props<{ error: string; queryKey: string; requestedCursor: string }>(),
);

export const selectFireSightIncident = createAction(
  '[FireSight] Select Incident',
  props<{ incidentUuid: string }>(),
);
export const loadFireSightIncidentContext = createAction(
  '[FireSight] Load Incident Context',
  props<{ incidentUuid: string }>(),
);
export const loadFireSightIncidentContextSuccess = createAction(
  '[FireSight] Load Incident Context Success',
  props<{
    incident: IFireSightIncidentDetail;
    observations: IFireSightObservation[];
    simulations: IFireSightSimulation[];
  }>(),
);
export const loadFireSightIncidentContextFailure = createAction(
  '[FireSight] Load Incident Context Failure',
  props<{ error: string }>(),
);

export const returnToFireSightOverview = createAction(
  '[FireSight] Return To Overview',
);
export const setFireSightActiveView = createAction(
  '[FireSight] Set Active View',
  props<{ activeView: Extract<TFireSightActiveView, 'monitoring' | 'simulation'> }>(),
);
export const selectFireSightObservation = createAction(
  '[FireSight] Select Observation',
  props<{ observationUuid: string }>(),
);
export const selectFireSightSimulation = createAction(
  '[FireSight] Select Simulation',
  props<{ simulationUuid: string }>(),
);
export const selectFireSightHorizon = createAction(
  '[FireSight] Select Horizon',
  props<{ horizonH: number }>(),
);
export const selectFireSightProduct = createAction(
  '[FireSight] Select Product',
  props<{ productCode: string }>(),
);

export const startFireSightSimulation = createAction(
  '[FireSight] Start Simulation',
  props<{ horizonH: number }>(),
);
export const startFireSightSimulationSuccess = createAction(
  '[FireSight] Start Simulation Success',
  props<{ simulation: IFireSightSimulation }>(),
);
export const startFireSightSimulationFailure = createAction(
  '[FireSight] Start Simulation Failure',
  props<{ error: string }>(),
);

export const loadFireSightSimulation = createAction(
  '[FireSight] Load Simulation',
  props<{ simulationUuid: string }>(),
);
export const loadFireSightSimulationSuccess = createAction(
  '[FireSight] Load Simulation Success',
  props<{ simulation: IFireSightSimulation }>(),
);
export const loadFireSightSimulationFailure = createAction(
  '[FireSight] Load Simulation Failure',
  props<{ error: string }>(),
);

export const fireSightWebSocketStatusChanged = createAction(
  '[FireSight WebSocket] Status Changed',
  props<{ change: IFireSightWebSocketStatusChange }>(),
);
export const fireSightWebSocketEventReceived = createAction(
  '[FireSight WebSocket] Event Received',
  props<{ event: IFireSightWebSocketEvent }>(),
);
export const upsertFireSightIncident = createAction(
  '[FireSight WebSocket] Upsert Incident',
  props<{ incident: IFireSightIncidentSummary }>(),
);
export const patchFireSightIncidentOperationalStatus = createAction(
  '[FireSight WebSocket] Patch Incident Operational Status',
  props<{ incidentUuid: string; operationalStatus: string | null }>(),
);
export const reconcileFireSight = createAction('[FireSight] Reconcile');
